/**
 * Wave 353 · route-level guards.
 *
 * Centralizes the role-based redirects so every field-only surface
 * (CampoPage, HojePage, future Wave 354+ pages) shares the same
 * contract: client/broker who deep-link or pre-cache the URL get
 * sent to `/owner` instead of seeing a half-rendered crew page.
 *
 * Hiding a route in BottomTabs and SideNav is NOT enough — the URL
 * is still typeable, sharable, and bookmarkable. The guard MUST run
 * inside the page component, not just at the chrome layer.
 *
 * Usage:
 *   import { useReadOnlyRedirect } from '@/lib/route-guards';
 *
 *   export default function CampoPage() {
 *     const guard = useReadOnlyRedirect();
 *     if (guard) return guard;
 *     return <CampoUx />;
 *   }
 *
 * Why a hook + early return rather than a wrapper component:
 * - Plays nicely with React.lazy / Suspense (the page module's
 *   default export stays a real component, not a guard wrapper).
 * - Keeps the signal at the call site so `lint:contracts` can grep
 *   for `useReadOnlyRedirect` inside CampoPage.tsx and HojePage.tsx
 *   directly. A wrapper component would be one indirection too many
 *   for the contract checker.
 */

import { useNavigate } from 'react-router-dom';
import { useEffect, useState, type ReactElement } from 'react';
import { useAuth } from '@/store/auth.store';
import { useT } from '@/hooks/useT';

/**
 * Returns a `<Navigate to="/owner" replace />` element when the
 * current user is `client` or `broker`, otherwise `null`. Pages
 * that want to be field-only call this and early-return the result.
 *
 * Read-only viewers without a user (logged-out) are not handled
 * here — the parent `<RequireAuth />` route boundary already
 * redirects them to `/login`. This guard only exists for the case
 * where an authenticated read-only viewer reaches a crew URL.
 */
/**
 * W138.6 — ReadOnlyRedirectBanner
 *
 * Shown while `useReadOnlyRedirect` waits 1.5s before navigating. Gives
 * the user a visible signal that the redirect is intentional (not a
 * blank flash). Same pattern as `RedirectWithToast` (codex-r8).
 */
function ReadOnlyRedirectBanner({ to = '/owner' }: { to?: string } = {}) {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(true);
  const { locale } = useT();
  const isEn = locale === 'en';

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setVisible(false);
      navigate(to, { replace: true });
    }, 1500);
    return () => window.clearTimeout(handle);
  }, [navigate, to]);

  if (!visible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed top-4 left-1/2 -translate-x-1/2 z-50 max-w-md w-[calc(100%-2rem)]"
    >
      <div className="rounded-lg border border-line bg-surface px-4 py-3 shadow-md flex items-start gap-3">
        <div className="text-sm text-ink flex-1">
          <strong className="font-medium">{isEn ? 'Restricted access.' : 'Acesso restrito.'}</strong>{' '}
          <span className="text-ink-muted">
            {isEn ? "You don't have permission to access this area." : 'Você não tem permissão para acessar esta área.'}
          </span>
        </div>
      </div>
    </div>
  );
}

export function useReadOnlyRedirect(): ReactElement | null {
  const role = useAuth((s) => s.user?.role);
  if (role === 'client' || role === 'broker') {
    return <ReadOnlyRedirectBanner />;
  }
  return null;
}

/**
 * Wave 355 Run C · project-shell read-only redirect.
 *
 * `/projects/:id` is the crew project shell — it hosts orçamento,
 * fvs, rnc, mão-de-obra, compras and other operational tabs. A
 * `client` or `broker` who hits the URL directly (deep-link, paste,
 * bookmark) shouldn't see the crew shell; they should land on the
 * owner-shaped surface at `/owner/projects/:id`, which is the
 * read-only counterpart for the same project id.
 *
 * Returns a `<Navigate to={`/owner/projects/${projectId}`} replace />`
 * element when the current user is `client`/`broker` AND a `projectId`
 * is present, otherwise `null`. Project IDs in this repo are stable
 * ULID-style identifiers (`01...`) without URL-unsafe characters, so
 * plain interpolation is acceptable.
 *
 * Usage (must run BEFORE `api.getProject(projectId)` / `useQuery`):
 *
 *     const redirect = useReadOnlyProjectRedirect(projectId);
 *     if (redirect) return redirect;
 */
export function useReadOnlyProjectRedirect(
  projectId: string | undefined,
): ReactElement | null {
  const role = useAuth((s) => s.user?.role);
  if ((role === 'client' || role === 'broker') && projectId) {
    // W157 · 2026-06-01 — banner+delay em vez de `<Navigate>` cru (padrão 32).
    return <ReadOnlyRedirectBanner to={`/owner/projects/${projectId}`} />;
  }
  return null;
}

/**
 * WAVE-152 (P0-C) · crew project-shell redirect.
 *
 * The PM project shell (`/projects/:id/*` — hero, mesas Plano / Compras /
 * Dinheiro / Entrega) is a management surface. `client`/`broker` are already
 * bounced to `/owner/projects/:id` by `useReadOnlyProjectRedirect`, but
 * `foreman`/`worker` had NO guard: a deep-link or the Campo back-button
 * dumped a mestre/pedreiro straight into the full PM shell, which they
 * should never see. CREW_NAV hides the tabs in the bottom-nav, but the URL
 * is still typeable / sharable / bookmarkable — so the guard MUST run inside
 * the page, mirroring the client/broker boundary.
 *
 * Returns a banner that bounces `foreman`/`worker` to `/campo` (their home,
 * `HOME_BY_ROLE.foreman`) with a visible, non-silent explanation (padrão 32:
 * redirect de acesso negado sempre com mensagem de saída). Otherwise null.
 *
 * Usage (must run BEFORE `api.getProject(projectId)` / `useQuery`):
 *
 *     const crewRedirect = useCrewProjectRedirect();
 *     if (crewRedirect) return crewRedirect;
 */
function CrewProjectRedirectBanner() {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(true);
  const { locale } = useT();
  const isEn = locale === 'en';

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setVisible(false);
      navigate('/campo', { replace: true });
    }, 1500);
    return () => window.clearTimeout(handle);
  }, [navigate]);

  if (!visible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed top-4 left-1/2 -translate-x-1/2 z-50 max-w-md w-[calc(100%-2rem)]"
    >
      <div className="rounded-lg border border-line bg-surface px-4 py-3 shadow-md flex items-start gap-3">
        <div className="text-sm text-ink flex-1">
          <strong className="font-medium">{isEn ? 'This is a management area.' : 'Esta área é de gestão.'}</strong>{' '}
          <span className="text-ink-muted">
            {isEn ? 'Returning to your field dashboard.' : 'Voltando pro seu painel do Campo.'}
          </span>
        </div>
      </div>
    </div>
  );
}

export function useCrewProjectRedirect(): ReactElement | null {
  const role = useAuth((s) => s.user?.role);
  if (role === 'foreman' || role === 'worker') {
    return <CrewProjectRedirectBanner />;
  }
  return null;
}

/**
 * Wave Onda3 · Broker-only route guard.
 *
 * `/broker` is the supplier-facing broker dashboard. Admins and PMs
 * who land on this URL should be redirected to `/projects` with an
 * informational state. The backend-level 403 already handles the case
 * where an authenticated user has no supplier record; this guard
 * handles the role mismatch at the front end before any API call.
 *
 * Returns an element for admin/PM/foreman/worker roles, null for broker/client.
 * (client can also reach /broker by bookmark — redirect to /owner).
 *
 * W157 · 2026-06-01 — antes usava `<Navigate>` cru (redirect silencioso,
 * padrão 32). Agora usa banner+delay igual a `ReadOnlyRedirectBanner` e
 * `CrewProjectRedirectBanner`: o usuário vê uma mensagem de saída antes de
 * ser teleportado.
 */
function BrokerRedirectBanner({ to }: { to: string }) {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(true);
  const { locale } = useT();
  const isEn = locale === 'en';

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setVisible(false);
      navigate(to, { replace: true });
    }, 1500);
    return () => window.clearTimeout(handle);
  }, [navigate, to]);

  if (!visible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed top-4 left-1/2 -translate-x-1/2 z-50 max-w-md w-[calc(100%-2rem)]"
    >
      <div className="rounded-lg border border-line bg-surface px-4 py-3 shadow-md flex items-start gap-3">
        <div className="text-sm text-ink flex-1">
          <strong className="font-medium">{isEn ? 'This area is for suppliers.' : 'Esta área é de fornecedores.'}</strong>{' '}
          <span className="text-ink-muted">
            {isEn ? 'Returning to your dashboard.' : 'Voltando pro seu painel.'}
          </span>
        </div>
      </div>
    </div>
  );
}

export function useBrokerOnlyRedirect(): ReactElement | null {
  const role = useAuth((s) => s.user?.role);
  if (!role) return null;
  // Roles that should NOT be on /broker:
  if (role === 'admin' || role === 'project_manager' || role === 'foreman' || role === 'worker') {
    return <BrokerRedirectBanner to="/projects" />;
  }
  if (role === 'client') {
    return <BrokerRedirectBanner to="/owner" />;
  }
  // broker → allow through
  return null;
}
